// §4.9 — SALES ANALYTICS, /sales/analytics (Module 09, P3-E PR-E3, D-293).
//
// WHAT IT IS. One row per manager, price tier and currency over a chosen
// range, plus the off-list trend line. The numbers come from `sales_stats_daily`
// (§2.21) up to the rollup's watermark and LIVE off the documents after it
// (§3.10, D-293 amendment (g)) — so a manager sees the quote they sent ten
// minutes ago, and the freshness line says which days are settled and which
// are still moving rather than presenting one number with two meanings.
//
// THE TIER ON EVERY ROW IS THE DOCUMENT'S, frozen when it was priced (D-293
// ruling 1). Re-tiering a customer changes what their next quote counts as and
// never rewrites a day already rolled up. That is the whole decision, and it
// is why this screen can be read as history at all.
//
// CURRENCY IS A KEY, NEVER A SUM (ruling 4, §9.13). A manager trading in two
// currencies gets two rows, and nothing on this screen adds across them. The
// fold that could have done it (`totalsByManager`) keeps currency in its key
// and says out loud that it does not.
//
// TWO ELEMENTS OF §4.9 ARE DELIBERATELY NOT NUMBERS HERE (ruling 3): average
// response time needs module 17's message timestamps, and the off-cut
// monetisation SHARE needs module 06's off-cut stock value as its denominator.
// Copying another module's facts into module 09's rollup is the cross-module
// read the constitution forbids, so each renders a stated absence — a sentence
// naming what is missing and why — never a zero, which a manager would read as
// a measurement.
//
// A RATE WITH NO DENOMINATOR IS NOT 0%. A manager who sent no quotes has no
// win rate; printing 0% would say "sent plenty, won none". `winRate` and
// `offListRate` answer null and the screen prints an em dash with a sentence.
//
// THE RATE OPENS (§10.13). A percentage nobody can open is a number to argue
// about, not a habit anyone can correct — so every off-list cell is a control
// that lists the lines behind it, with the rate the engine RESOLVED beside the
// rate the manager TYPED. The list is fetched only when a reader asks for it:
// it names customers' documents and their prices, and rendering a percentage
// is no reason to pull them.
//
// NO CHARTING LIBRARY (ruling 8, doctrine 4): the trend is inline SVG, the
// same idiom the quality analytics screen already uses.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { FilterBar } from "../../../../components/filter-bar";
import { useDesktop } from "../../../../components/use-desktop";
import { PageHeader } from "../../../../components/breadcrumbs";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  ForbiddenState,
} from "../../../../components/states";
import { formatBusinessDate } from "../../../../lib/datetime";
import { formatRatioAsPercent } from "../../../../lib/quantity";
import { useTenantTimezone } from "../../../../lib/role-codes";
import { formatMinor } from "../lib/money";
import { requestIdOf } from "../../../../lib/request";
import { canOpenAnalytics, isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import { ForecastLinkCard } from "../components/pipeline/forecast-link-card";
import { PipelineTabs } from "../components/pipeline/pipeline-parts";
import { useOwnerIndex } from "../hooks/sales-queries";
import { useMemberRefAsk } from "../lib/reads";
import {
  offListRate,
  offListTrend,
  priceDelta,
  totalsByManager,
  useOffListLines,
  useSalesStats,
  winRate,
  type ManagerTotals,
  type SalesOffListLine,
} from "../hooks/sales-stats-queries";
import { bookClientPager } from "../hooks/book-pages";

/** `YYYY-MM-DD` for an instant in the TENANT's zone — never the browser's.
 * D-203 is the precedent: §4.4's `wanted_date` was the browser's day while the
 * save priced at the tenant's, and the two disagreed for anyone not sitting in
 * the plant. A range computed here has the same failure mode. */
function tenantDate(at: Date, zone: string | undefined): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zone ?? "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

function shiftDays(date: string, days: number): string {
  const at = new Date(`${date}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/** A rate as the reader's locale writes a percentage ("38.9%", "38,9 %"). */
export function formatRate(rate: number | null, dash: string, locale: string): string {
  if (rate === null) return dash;
  return formatRatioAsPercent(rate, { locale });
}

export function SalesAnalyticsScreen() {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const zone = useTenantTimezone();
  const desktop = useDesktop();
  // §4.9's audience, from the SAME predicate the nav strip reads — a screen
  // whose entry and whose refusal disagree is how a dead link or an
  // unreachable screen is born.
  const allowed = canOpenAnalytics(perms);
  const refused = isSalesReadRefused(perms, allowed);

  const today = tenantDate(new Date(), zone);
  const [from, setFrom] = useState(() => shiftDays(today, -29));
  const [to, setTo] = useState(() => today);
  const [tier, setTier] = useState<"" | "market" | "b2b" | "distributor">("");
  /** The row whose off-list lines are open, or null. One at a time: the panel
   * answers "which lines are THIS manager's", and two open lists would be one
   * list that no longer says whose. */
  const [drilled, setDrilled] = useState<ManagerTotals | null>(null);

  const query = useSalesStats(
    { from, to, ...(tier === "" ? {} : { tier }) },
    allowed && !refused && from <= to,
  );

  const owners = useOwnerIndex(perms.isAdmin);
  const memberRef = useMemberRefAsk(owners.isPending);
  const rows = useMemo(() => query.data?.items ?? [], [query.data]);
  const totals = useMemo(() => totalsByManager(rows), [rows]);
  const trend = useMemo(() => offListTrend(rows), [rows]);

  if (refused) {
    return <ForbiddenState title={t("sales.access.title")} body={t("sales.access.analytics")} />;
  }
  if (query.isPending && query.fetchStatus !== "idle") {
    return <CardSkeleton label={t("sales.analytics.loading")} />;
  }
  if (query.isError) {
    return (
      <ErrorState
        title={t("sales.analytics.error")}
        retryLabel={t("sales.common.retry")}
        requestId={requestIdOf(query.error)}
        requestIdLabel={t("sales.common.requestId")}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const valuesVisible = query.data?.values_visible ?? false;
  const through = query.data?.materialized_through ?? null;

  const columns: DataTableColumn<ManagerTotals>[] = [
    {
      key: "manager",
      header: t("sales.analytics.colManager"),
      // A name, never the uuid: the member directory names it for an admin.
      // Anyone else is narrowed by the server to their own rows, which read
      // "You".
      render: (r) => (
        <span data-kind="manager">
          {!perms.isAdmin ? (
            t("sales.analytics.managerYou")
          ) : (
            <RefLabel
              value={owners.byId.get(r.ownerUserId)}
              id={r.ownerUserId}
              pending={owners.isPending}
              {...memberRef}
            />
          )}
        </span>
      ),
    },
    {
      key: "tier",
      header: t("sales.analytics.colTier"),
      render: (r) => <span data-kind="tier">{t(`sales.tier.${r.tier}`)}</span>,
    },
    { key: "currency", header: t("sales.analytics.colCurrency"), render: (r) => r.currency },
    {
      key: "sent",
      header: t("sales.analytics.colQuotesSent"),
      numeric: true,
      render: (r) => r.quotesSent,
    },
    {
      key: "win",
      header: t("sales.analytics.colWinRate"),
      numeric: true,
      render: (r) => (
        <span
          data-kind="win-rate"
          title={r.quotesSent === 0 ? t("sales.analytics.noRateHint") : undefined}
        >
          {formatRate(
            winRate(r.quotesSent, r.quotesAccepted),
            t("sales.analytics.noRate"),
            i18n.language,
          )}
        </span>
      ),
    },
    {
      key: "offlist",
      header: t("sales.analytics.colOffListRate"),
      numeric: true,
      // THE RATE IS THE DOOR (§10.13). A row with no off-list line has nothing
      // to open, so it renders the figure alone rather than a control that
      // would answer with an empty list — the D-088 shape.
      render: (r) =>
        r.offListLines === 0 ? (
          <span data-kind="off-list-rate">
            {formatRate(
              offListRate(r.pricedLines, r.offListLines),
              t("sales.analytics.noRate"),
              i18n.language,
            )}
          </span>
        ) : (
          <button
            type="button"
            className="ds-lnk"
            data-kind="off-list-rate"
            data-testid={`stats-drill-${r.ownerUserId}-${r.tier}-${r.currency}`}
            aria-expanded={drilled?.ownerUserId === r.ownerUserId && drilled.tier === r.tier}
            onClick={() => {
              setDrilled((open) =>
                open?.ownerUserId === r.ownerUserId &&
                open.tier === r.tier &&
                open.currency === r.currency
                  ? null
                  : r,
              );
            }}
          >
            {formatRate(
              offListRate(r.pricedLines, r.offListLines),
              t("sales.analytics.noRate"),
              i18n.language,
            )}
          </button>
        ),
    },
    {
      key: "breaches",
      header: t("sales.analytics.colBreaches"),
      numeric: true,
      render: (r) => r.marginBreachLines,
    },
    {
      key: "orders",
      header: t("sales.analytics.colOrders"),
      numeric: true,
      render: (r) => r.ordersConfirmed,
    },
    {
      key: "value",
      kind: "money",
      header: t("sales.analytics.colOrderValue"),
      numeric: true,
      // NULL is the §7 redaction, not zero — the dash and the note under the
      // table say which, and `values_visible` is what decides, never "the
      // first row happened to be null".
      render: (r) =>
        r.orderValueMinor === null
          ? t("sales.analytics.noRate")
          : formatMinor(r.orderValueMinor, r.currency, i18n.language),
    },
    {
      key: "offcut",
      kind: "money",
      header: t("sales.analytics.colOffcutValue"),
      numeric: true,
      render: (r) =>
        r.offcutValueMinor === null
          ? t("sales.analytics.noRate")
          : formatMinor(r.offcutValueMinor, r.currency, i18n.language),
    },
  ];

  const tierLabel = (value: typeof tier): string =>
    value === "" ? t("sales.analytics.allTiers") : t(`sales.tier.${value}`);

  return (
    <div className="sales-analytics-screen" data-testid="sales-analytics">
      <PageHeader title={t("sales.analytics.title")} lede={t("sales.analytics.intro")} />

      {/* D-445: Analytics is the pipeline strip's fourth tab, so the forecast
          is one press away. A reader who sees no strip keeps the link card. */}
      {perms.canOpenPipeline ? (
        <PipelineTabs active="analytics" shared={{}} />
      ) : (
        <ForecastLinkCard />
      )}

      <FilterBar
        data-testid="stats-filters"
        filters={{
          inline: (
            <>
              <Field label={t("sales.analytics.rangeFrom")}>
                <input
                  id="stats-from"
                  className="ds-input"
                  type="date"
                  value={from}
                  max={to}
                  data-testid="stats-from"
                  onChange={(e) => {
                    setFrom(e.target.value);
                  }}
                />
              </Field>
              <Field label={t("sales.analytics.rangeTo")}>
                <input
                  id="stats-to"
                  className="ds-input"
                  type="date"
                  value={to}
                  min={from}
                  max={today}
                  data-testid="stats-to"
                  onChange={(e) => {
                    setTo(e.target.value);
                  }}
                />
              </Field>
            </>
          ),
          more: (
            <Field label={t("sales.analytics.tierFilter")}>
              <Select
                id="stats-tier"
                value={tier}
                data-testid="stats-tier"
                onChange={(e) => {
                  setTier(e.target.value as typeof tier);
                }}
              >
                <option value="">{t("sales.analytics.allTiers")}</option>
                <option value="market">{t("sales.tier.market")}</option>
                <option value="b2b">{t("sales.tier.b2b")}</option>
                <option value="distributor">{t("sales.tier.distributor")}</option>
              </Select>
            </Field>
          ),
          label:
            tier === "" ? t("sales.common.filters") : t("sales.common.filtersCount", { count: 1 }),
          title: t("sales.common.filtersTitle"),
          doneLabel: t("sales.common.filtersDone"),
        }}
        chips={
          tier === ""
            ? []
            : [
                {
                  key: "tier",
                  label: t("sales.common.filterChip", {
                    filter: t("sales.analytics.tierFilter"),
                    value: tierLabel(tier),
                  }),
                  onRemove: () => {
                    setTier("");
                  },
                },
              ]
        }
        removeLabel={(chip) => t("sales.common.filterRemove", { filter: chip.label })}
      />
      {/* Below 769 px the two dates move into the sheet, so the range the
          numbers cover is said here instead. */}
      {desktop ? null : (
        <p className="sales-hint" data-testid="stats-range">
          {t("sales.analytics.rangeShown", {
            from: formatBusinessDate(from, i18n.language),
            to: formatBusinessDate(to, i18n.language),
          })}
        </p>
      )}

      {totals.length === 0 ? (
        <EmptyState title={t("sales.analytics.empty")} body={t("sales.analytics.emptyBody")} />
      ) : (
        <Card>
          <DataTable
            clientPager={bookClientPager(t, i18n.language)}
            caption={t("sales.analytics.tableCaption")}
            columns={columns}
            rows={totals}
            rowKey={(r) => `${r.ownerUserId}|${r.currency}`}
            rowTestId="stats-row"
            empty={t("sales.analytics.empty")}
          />
          {!valuesVisible && (
            <Card.Body>
              <p className="sales-hint" data-testid="stats-values-hidden">
                {t("sales.analytics.valuesHidden")}
              </p>
            </Card.Body>
          )}
        </Card>
      )}
      <p className="sales-hint" data-testid="stats-freshness">
        {through === null
          ? t("sales.analytics.freshnessNone")
          : t("sales.analytics.freshness", { date: through })}
      </p>

      {drilled !== null && (
        <OffListPanel
          row={drilled}
          from={from}
          to={to}
          onClose={() => {
            setDrilled(null);
          }}
        />
      )}

      <Card title={t("sales.analytics.trendTitle")} data-testid="stats-trend-card">
        <Card.Body>
          <p className="sales-hint">{t("sales.analytics.trendBody")}</p>
          <OffListTrend points={trend} emptyLabel={t("sales.analytics.trendEmpty")} />
        </Card.Body>
      </Card>
    </div>
  );
}

/**
 * §10.13's DRILL-THROUGH — the lines one manager's off-list rate is made of.
 *
 * WHAT IT ANSWERS that the rate cannot: which document, which line, what the
 * §3.2 engine RESOLVED and what the manager TYPED instead. That difference is
 * the whole finding — a lead who can name the four lines can have the
 * conversation, and a lead holding a percentage can only have an argument.
 *
 * IT FETCHES ONLY WHEN OPENED. The list carries customers' document numbers
 * and their prices; rendering a percentage is no reason to pull them.
 *
 * A REDACTED PRICE IS A DASH, NEVER A ZERO, and the delta is then absent
 * rather than computed against nothing (`priceDelta` answers null) — the same
 * rule the table above follows for money.
 */
export function OffListPanel({
  row,
  from,
  to,
  onClose,
}: {
  row: ManagerTotals;
  from: string;
  to: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const query = useOffListLines({ from, to, ownerUserId: row.ownerUserId, tier: row.tier }, true);

  const columns: DataTableColumn<SalesOffListLine>[] = [
    {
      key: "document",
      header: t("sales.analytics.drillDocument"),
      kind: "id",
      render: (l) => <span data-kind="drill-doc">{l.document_no}</span>,
    },
    {
      key: "line",
      header: t("sales.analytics.drillLine"),
      numeric: true,
      render: (l) => l.line_no,
    },
    {
      key: "dated",
      header: t("sales.analytics.drillDated"),
      kind: "date",
      render: (l) => l.dated_on,
    },
    { key: "what", header: t("sales.analytics.drillWhat"), render: (l) => l.description },
    { key: "qty", header: t("sales.analytics.drillQty"), numeric: true, render: (l) => l.qty },
    {
      key: "resolved",
      kind: "money",
      header: t("sales.analytics.drillResolved"),
      numeric: true,
      render: (l) =>
        // Absent (§7, D-337 ruling 1) and null (no list rate) both print the dash.
        l.list_price_minor === null || l.list_price_minor === undefined
          ? t("sales.analytics.noRate")
          : formatMinor(l.list_price_minor, l.currency, i18n.language),
    },
    {
      key: "typed",
      kind: "money",
      header: t("sales.analytics.drillTyped"),
      numeric: true,
      render: (l) => (
        <span data-kind="drill-typed">
          {l.unit_price_minor === undefined
            ? t("sales.analytics.noRate")
            : formatMinor(l.unit_price_minor, l.currency, i18n.language)}
        </span>
      ),
    },
    {
      key: "delta",
      kind: "money",
      header: t("sales.analytics.drillDelta"),
      numeric: true,
      render: (l) => {
        const delta = priceDelta(l);
        if (delta === null) return t("sales.analytics.noRate");
        // The SIGN is the point and it is carried by the formatted number, so
        // a reader can tell a discount from an uplift at a glance.
        return (
          <span data-kind="drill-delta" data-sign={delta < 0 ? "down" : "up"}>
            {formatMinor(delta, l.currency, i18n.language)}
          </span>
        );
      },
    },
    {
      key: "breach",
      header: t("sales.analytics.drillBreach"),
      render: (l) =>
        l.margin_breached ? (
          <span data-kind="drill-breach">{t("sales.analytics.drillBreached")}</span>
        ) : (
          ""
        ),
    },
  ];

  return (
    <Card
      title={t("sales.analytics.drillTitle", { tier: t(`sales.tier.${row.tier}`) })}
      action={
        <Button size="sm" onClick={onClose}>
          {t("sales.common.close")}
        </Button>
      }
    >
      {query.isError ? (
        <ErrorState
          title={t("sales.analytics.drillError")}
          retryLabel={t("sales.common.retry")}
          requestId={requestIdOf(query.error)}
          requestIdLabel={t("sales.common.requestId")}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <>
          <DataTable
            clientPager={bookClientPager(t, i18n.language)}
            caption={t("sales.analytics.drillCaption")}
            columns={columns}
            rows={query.data?.items ?? []}
            rowKey={(l) => `${l.document_id}|${String(l.line_no)}`}
            rowTestId="stats-drill-row"
            loading={query.isPending}
            loadingLabel={t("sales.common.loading")}
            empty={t("sales.analytics.drillEmpty")}
          />
          {query.data?.truncated === true && (
            <Card.Body>
              <p className="sales-hint" data-testid="stats-drill-truncated">
                {t("sales.analytics.drillTruncated")}
              </p>
            </Card.Body>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * The off-list trend, as inline SVG (ruling 8 — no charting library).
 *
 * A DAY WITH NO PRICED LINE IS A GAP, not a zero: the polyline breaks rather
 * than dropping to the floor, because a manager who quoted nothing on Sunday
 * did not achieve a 0% off-list rate that day. Each drawn day also gets a
 * `<title>`, so the figures are reachable without hovering pixels.
 */
export function OffListTrend({
  points,
  emptyLabel,
}: {
  points: readonly { statDate: string; rate: number | null }[];
  emptyLabel: string;
}) {
  const { i18n } = useTranslation();
  const drawn = points.filter((p) => p.rate !== null);
  if (drawn.length === 0) {
    return (
      <p className="sales-hint" data-testid="stats-trend-empty">
        {emptyLabel}
      </p>
    );
  }
  const W = 320;
  const H = 72;
  const step = points.length > 1 ? W / (points.length - 1) : 0;
  // Runs of consecutive drawn days become separate polylines, so a gap is a
  // BREAK in the line rather than a segment drawn through missing days.
  const runs: { x: number; y: number }[][] = [];
  let run: { x: number; y: number }[] = [];
  points.forEach((p, i) => {
    if (p.rate === null) {
      if (run.length > 0) runs.push(run);
      run = [];
      return;
    }
    run.push({ x: i * step, y: H - p.rate * H });
  });
  if (run.length > 0) runs.push(run);

  return (
    <svg
      className="sales-trend"
      viewBox={`0 0 ${String(W)} ${String(H)}`}
      role="img"
      aria-label={emptyLabel}
      data-testid="stats-trend"
    >
      {runs.map((r, i) => (
        <polyline
          key={String(i)}
          fill="none"
          strokeWidth="2"
          points={r.map((pt) => `${String(pt.x)},${String(pt.y)}`).join(" ")}
        />
      ))}
      {points.map((p) =>
        p.rate === null ? null : (
          <title key={p.statDate}>
            {`${formatBusinessDate(p.statDate, i18n.language)}: ${formatRatioAsPercent(p.rate, {
              locale: i18n.language,
            })}`}
          </title>
        ),
      )}
    </svg>
  );
}
